import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AssignQPToFillSiteCommand,
  AssignSourceSitesToFillSiteCommand,
  AssignUserToFillSiteCommand,
  BatchQueryResultViewModel,
  CompaniesClient,
  CreateFillSiteCommand,
  EditFillSiteCommand,
  FillSiteCleanFillUsersClient,
  FillSiteQPSearchViewModelTotalTypePagedResultResponse,
  FillSiteQualifiedPersonsClient,
  FillSitesClient,
  FillSiteSearchQuery,
  FillSiteSearchViewModelSiteTotalTypePagedResultResponse,
  FillSiteSourceSitesClient,
  FillSiteSourceSiteSearchViewModelFillSiteSourceSiteTotalTypePagedResultResponse,
  FillSiteUserSearchViewModelTotalTypePagedResultResponse,
  FillSiteViewModel,
  IQualifiedPersonViewModel,
  IUserSearchViewModel,
  SourceSitesClient
} from '@services/cleanfill-api/cleanfill-api.generated';
import { AuthService } from '@services/core/auth.service';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { SearchQuery } from '@view-models/search-query/search-query';
import { Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HttpFillSitesService {
  constructor(
    private sourceSiteClient: SourceSitesClient,
    private fillSiteClient: FillSitesClient,
    private fillSiteUserClient: FillSiteCleanFillUsersClient,
    private fillSiteSourceSitesClient: FillSiteSourceSitesClient,
    private fillSiteQp: FillSiteQualifiedPersonsClient,
    private companiesClient: CompaniesClient,
    private authService: AuthService,
    private http: HttpClient
  ) { }

  getFillSites(query: ISearchQuery): Observable<FillSiteSearchViewModelSiteTotalTypePagedResultResponse> {
    const fillQuery = new FillSiteSearchQuery({
      filterBy: query.FilterBy,
      sortBy: query.SortBy.length ? query.SortBy : undefined,
      page: query.Page,
      pageSize: query.PageSize,
    });
    return this.authService.activeUserLegalEntityUniqueId$.pipe(
      concatMap((uniqueId) => this.companiesClient.searchFillSite(uniqueId, undefined, fillQuery))
    );
  }
  getALLFillSites(query: ISearchQuery): Observable<FillSiteSearchViewModelSiteTotalTypePagedResultResponse> {
    return this.fillSiteClient.searchFillSites(query.Page, query.PageSize, query.SortBy, query.FilterBy, undefined);
  }
  getFillSitesById(uniqueId: string, query: ISearchQuery): Observable<FillSiteSearchViewModelSiteTotalTypePagedResultResponse> {
    const fillQuery = new FillSiteSearchQuery({
      filterBy: query.FilterBy,
      sortBy: query.SortBy.length ? query.SortBy : undefined,
      page: query.Page,
      pageSize: query.PageSize,
    });
    return this.companiesClient.searchFillSite(uniqueId, undefined, fillQuery);
  }
  getFillSitesBySourceSite(uniqueId: string, query: ISearchQuery): Observable<FillSiteSearchViewModelSiteTotalTypePagedResultResponse> {
    return this.sourceSiteClient.searchSourceSiteFillSites(
      uniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }
  getFillSiteQps(fillSiteUniqueId: string): Observable<IQualifiedPersonViewModel[]> {
    return this.fillSiteClient.getFillSiteQualifiedPersons(fillSiteUniqueId, new Date().getTimezoneOffset().toString());
  }

  getBatches(fillSiteUniqueId: string): Observable<BatchQueryResultViewModel[]> {
    return this.fillSiteClient.getFillSiteBatches(fillSiteUniqueId, new Date().getTimezoneOffset().toString());
  }

  createFillSite(payload: CreateFillSiteCommand): Observable<FillSiteViewModel> {
    return this.fillSiteClient.createFillSite(undefined, payload);
  }
  assignSourceSitesToFillSite(payload: AssignSourceSitesToFillSiteCommand) {
    return this.fillSiteSourceSitesClient.assignSourceSitesToFillSite(undefined, payload);
  }

  editFillSite(payload: EditFillSiteCommand): Observable<void> {
    return this.fillSiteClient.editFillSite(payload.uniqueId, undefined, payload);
  }

  deleteFillSite(uniqueId: string): Observable<void> {
    return this.fillSiteClient.deleteFillSite(uniqueId, undefined);
  }

  archiveFillSite(uniqueId: string): Observable<void> {
    return this.fillSiteClient.archiveFillSite(uniqueId, undefined);
  }

  reactivateFillSite(uniqueId: string): Observable<void> {
    return this.fillSiteClient.reactivateFillSite(uniqueId, undefined);
  }

  uploadAttachments(data: FormData) {
    return this.http.post<number>('/api/FillSites/Attachment', data);
  }

  getAttachments(siteUniqueId: string) {
    return this.fillSiteClient.getFillSiteAttachmentDetail(siteUniqueId, undefined);
  }

  getFillSite(fillSiteUniqueId: string) {
    return this.fillSiteClient.getFillSiteDetails(fillSiteUniqueId, undefined);
  }

  getUsers(fillSiteUniqueId: string, query: SearchQuery): Observable<FillSiteUserSearchViewModelTotalTypePagedResultResponse> {
    return this.fillSiteClient.getFillSiteAssignedUsers(
      fillSiteUniqueId,
      fillSiteUniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      undefined
    );
  }
  getFillSiteSourceSites(
    fillSiteUniqueId: string,
    query: SearchQuery
  ): Observable<FillSiteSourceSiteSearchViewModelFillSiteSourceSiteTotalTypePagedResultResponse> {
    return this.fillSiteClient.getFillSiteSourceSites(
      fillSiteUniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      undefined
    );
  }

  getAssignedQualifiedPersons(
    fillSiteUniqueId: string,
    query: SearchQuery
  ): Observable<FillSiteQPSearchViewModelTotalTypePagedResultResponse> {
    return this.fillSiteClient.getFillSiteAssignedQPs(
      fillSiteUniqueId,
      fillSiteUniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      undefined
    );
  }

  assignUser(fillSiteUniqueId: string, user: IUserSearchViewModel): Observable<void> {
    return this.fillSiteUserClient.assignUserToFillSite(
      undefined,
      new AssignUserToFillSiteCommand({ cleanFillUserUniqueId: user.uniqueId, fillSiteUniqueId })
    );
  }

  unassignUser(uniqueId: string): Observable<void> {
    return this.fillSiteUserClient.unassignUserFromFillSite(uniqueId, undefined);
  }

  assignQp(fillSiteUniqueId: string, qualifiedPersonUniqueId: string): Observable<void> {
    return this.fillSiteQp.assignQPToFillSite(undefined, new AssignQPToFillSiteCommand({ fillSiteUniqueId, qualifiedPersonUniqueId }));
  }

  unassignQp(qpUniqueId: string): Observable<void> {
    return this.fillSiteQp.unassignQPFromFillSite(qpUniqueId, undefined);
  }
}
